import { Pencil2Icon, PlusCircledIcon, TrashIcon } from '@radix-ui/react-icons'
import { Popover } from '@radix-ui/themes'
import { Button, Spin, Tooltip } from 'antd'
import {
  AssetOverviewAdminGate,
  AssetOverviewEditorGate,
} from 'features/assetDatabase'
import { useRolodexEdit } from 'features/fde'
import { RightSideDrawerProps } from 'features/rightDrawer/utils/interfaces'
import { useRightDrawer } from 'features/rightDrawer/utils/RightDrawerProvider'
import { RolodexEntryQueryBody, useRolodexEntries } from 'features/rolodex'
import { ReactNode, useState } from 'react'
import styled from 'styled-components'
import {
  GeckoAssetModel,
  GeckoComponent,
  GeckoEntryBase,
  GeckoUnit,
  getSortedAssetModels,
  RolodexEntryJson,
} from 'types'
import { compareAlphanumeric } from 'utils/stringHelpers'

import { AddUnitRichAssetModel } from './AddUnitRichAssetModel'
import { EditGeckoComponent } from './EditGeckoComponent'
import { EditGeckoRichAssetModel } from './EditGeckoRichAssetModel'

const StyledHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const StyledHeader = styled.div`
  font-size: 16px;
`

const StyledAddComponentButton = styled(Button)`
  background: transparent;
`

const StyledComponentCard = styled.div`
  height: 60px;
  background-color: ${({ theme }) => theme.forecast.background3};
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 5px;
  padding: 10px;
`

const StyledComponentEditButton = styled(Button)`
  background-color: ${({ theme }) => theme.forecast.background4};
`

const StyledComponentDeleteButton = styled(StyledComponentEditButton)`
  &:hover {
    background-color: red;
  }
`

const StyledSpinContainer = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`

const StyledActionContainer = styled.div`
  display: flex;
  gap: 5px;
`

const StyledPopoverContent = styled(Popover.Content)`
  border: 2px solid ${({ theme }) => theme.forecast.background4};
  background-color: ${({ theme }) => theme.forecast.background3};
  color: ${({ theme }) => theme.forecast.text1};
  padding: 0;
  border-radius: 0.25rem;
  max-width: 300px;
  z-index: 1000;
`

export const EditGeckoComponentDrawerProps: RightSideDrawerProps = {
  title: 'Edit Gecko Component',
  width: '600px',
}

export const EditGeckoComponentDrawer = () => {
  const { drawerEntry } = useRightDrawer()
  if (!drawerEntry) {
    throw new Error(
      'Attempted to load Edit Component without providing a unit!'
    )
  }
  return <EditGeckoComponentAndRichAssetModel unit={drawerEntry} />
}

enum EditGeckoComponentState {
  SelectComponent,
  EditComponent,
  AddComponent,
  EditRichAssetModel,
  AddRichAssetModel,
}

enum ComponentModelAction {
  Add,
  Edit,
  Delete,
}

interface EditGeckoComponentAndRichAssetModelInterface {
  unit: GeckoEntryBase
}

interface ComponentModelText {
  buttonContent: ReactNode
  tooltipText: string
}

export const EditGeckoComponentAndRichAssetModel = ({
  unit,
}: EditGeckoComponentAndRichAssetModelInterface) => {
  // Before we add any components, we need to determine if there is an asset model for this unit
  // If there is, we can add a new component to it. Otherwise, we need to create a RAM and then add the component to it
  const { assetModels, components, forceRefresh, isLoading } =
    useRolodexEntries({
      type: ['asset-model', 'component'],
      tags: { unit: unit.id },
    } as RolodexEntryQueryBody)

  const { rolodexPut } = useRolodexEdit()

  // The state of the edit drawer (are we selecting a component, adding a new one, or editing an existing one)
  const [currentState, setCurrentState] = useState<EditGeckoComponentState>(
    EditGeckoComponentState.SelectComponent
  )
  // The component we want to edit (should only be populated when we are going into the `EditComponent` state above
  const [componentToEdit, setComponentToEdit] = useState<
    GeckoComponent | undefined
  >(undefined)

  const [bypassRAMCreation, setBypassRAMCreation] = useState<boolean>(false)

  if (isLoading) {
    return (
      <StyledSpinContainer>
        <Spin />
      </StyledSpinContainer>
    )
  }

  // If we didn't get any asset model for this component's unit, we need to determine what type it should be
  // If we don't have the proper asset model type for this unit, allow us to bypass
  if (
    assetModels.length < 1 &&
    !bypassRAMCreation &&
    currentState !== EditGeckoComponentState.AddRichAssetModel
  ) {
    setCurrentState(EditGeckoComponentState.AddRichAssetModel)
  }

  // Get the most recent asset model based on effective date
  const assetModel: GeckoAssetModel = getSortedAssetModels([...assetModels])[0]

  const buildComponentCard = (
    component: GeckoComponent,
    assetModel: GeckoAssetModel | undefined
  ) => {
    const componentOnAssetModel = !!assetModel?.data.components.has(
      component.id
    )

    const ButtonTextMapping = new Map<ComponentModelAction, ComponentModelText>(
      [
        [
          ComponentModelAction.Edit,
          {
            tooltipText: 'Edit Component Model',
            buttonContent: <Pencil2Icon />,
          },
        ],
        [
          ComponentModelAction.Add,
          {
            tooltipText: 'Add Component Model',
            buttonContent: <PlusCircledIcon />,
          },
        ],
        [
          ComponentModelAction.Delete,
          {
            tooltipText: 'Delete Component Model',
            buttonContent: <TrashIcon />,
          },
        ],
      ]
    )

    const addEditButtonDetails = componentOnAssetModel
      ? ButtonTextMapping.get(ComponentModelAction.Edit)
      : ButtonTextMapping.get(ComponentModelAction.Add)

    const deleteButtonDetails = ButtonTextMapping.get(
      ComponentModelAction.Delete
    )

    const deleteComponentFromAssetModel = (
      componentID: string,
      assetModel: GeckoAssetModel
    ) => {
      const rawAssetModel: RolodexEntryJson = assetModel.raw
      const rawAssetModelComponents: Record<string, unknown> = rawAssetModel
        .data.components as Record<string, unknown>
      if (!rawAssetModelComponents[componentID]) {
        return rawAssetModel
      }
      delete rawAssetModelComponents[componentID]
      return rawAssetModel
    }

    return (
      <StyledComponentCard key={component.id} data-testid={component.id}>
        <div>{component.data.name}</div>
        <AssetOverviewEditorGate>
          <StyledActionContainer>
            <Tooltip title={addEditButtonDetails?.tooltipText}>
              <StyledComponentEditButton
                data-testid={addEditButtonDetails?.tooltipText}
                onClick={() => {
                  setComponentToEdit(component)
                  if (bypassRAMCreation) {
                    setCurrentState(EditGeckoComponentState.EditComponent)
                  } else {
                    setCurrentState(EditGeckoComponentState.EditRichAssetModel)
                  }
                }}>
                {addEditButtonDetails?.buttonContent}
              </StyledComponentEditButton>
            </Tooltip>
            {assetModel && componentOnAssetModel && (
              <Popover.Root>
                <Popover.Trigger>
                  <StyledComponentDeleteButton>
                    {deleteButtonDetails?.buttonContent}
                  </StyledComponentDeleteButton>
                </Popover.Trigger>
                <StyledPopoverContent>
                  <div>
                    <div style={{ textAlign: 'center' }}>
                      Are you sure you want to delete {component.data.name} from
                      the Asset Model? This cannot be undone.
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'center',
                        paddingTop: '10px',
                        gap: '10px',
                      }}>
                      <StyledComponentEditButton
                        onClick={() => {
                          if (assetModel instanceof GeckoAssetModel) {
                            const editedAssetModel =
                              deleteComponentFromAssetModel(
                                component.id,
                                assetModel
                              )
                            rolodexPut([editedAssetModel]).then(() =>
                              forceRefresh()
                            )
                          } else {
                            throw new Error(
                              'Unable to edit this component. The asset model was unexpectedly undefined!'
                            )
                          }
                        }}>
                        Confirm
                      </StyledComponentEditButton>
                    </div>
                  </div>
                </StyledPopoverContent>
              </Popover.Root>
            )}
          </StyledActionContainer>
        </AssetOverviewEditorGate>
      </StyledComponentCard>
    )
  }

  if (currentState === EditGeckoComponentState.SelectComponent) {
    return (
      <>
        <StyledHeaderContainer>
          <StyledHeader>Components</StyledHeader>
          <AssetOverviewAdminGate>
            <StyledAddComponentButton
              onClick={() => {
                setCurrentState(EditGeckoComponentState.AddComponent)
              }}>
              <PlusCircledIcon data-testid='add-new-component-button' />
            </StyledAddComponentButton>
          </AssetOverviewAdminGate>
        </StyledHeaderContainer>
        {components
          .toSorted((a, b) => compareAlphanumeric(a.data.name, b.data.name))
          .map((component) => buildComponentCard(component, assetModel))}
      </>
    )
  } else if (currentState === EditGeckoComponentState.AddComponent) {
    return (
      <EditGeckoComponent
        unit={unit as GeckoUnit}
        refreshData={forceRefresh}
        returnToSelectComponent={() =>
          setCurrentState(EditGeckoComponentState.SelectComponent)
        }
      />
    )
  } else if (
    currentState === EditGeckoComponentState.EditComponent &&
    !!componentToEdit
  ) {
    return (
      <EditGeckoComponent
        unit={unit as GeckoUnit}
        refreshData={forceRefresh}
        returnToSelectComponent={() =>
          setCurrentState(EditGeckoComponentState.SelectComponent)
        }
        component={componentToEdit}
      />
    )
  } else if (
    currentState === EditGeckoComponentState.EditRichAssetModel &&
    assetModel &&
    !!componentToEdit
  ) {
    return (
      <EditGeckoRichAssetModel
        assetModel={assetModel}
        component={componentToEdit}
        refreshData={forceRefresh}
        goToEditComponent={() =>
          setCurrentState(EditGeckoComponentState.EditComponent)
        }
        goToSelectComponent={() =>
          setCurrentState(EditGeckoComponentState.SelectComponent)
        }
      />
    )
  } else if (
    currentState === EditGeckoComponentState.AddRichAssetModel &&
    !bypassRAMCreation
  ) {
    return (
      <AddUnitRichAssetModel
        unit={unit as GeckoUnit}
        refreshData={forceRefresh}
        bypassRAMCreation={(val: boolean) => setBypassRAMCreation(val)}
        goToSelectComponent={() => {
          setCurrentState(EditGeckoComponentState.SelectComponent)
        }}
      />
    )
  } else {
    return (
      <div>
        Bad Edit Component State! Current State is{' '}
        {EditGeckoComponentState[currentState]}, component{' '}
        {componentToEdit ? 'is' : 'is not'} defined, and{' '}
        {bypassRAMCreation ? 'is' : 'is not'} bypassed.
      </div>
    )
  }
}
